import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableHeader, TableRow,
} from '@/components/ui/Table';
import Icon from '@/components/ui/Icon';
import cn from '@/utils/cn';
import { dataCellClass, dataFrameClass, dataRowClass } from '@/components/admin/SysadminSPA/shared/SPADataTable';
import type { CompletionRecord } from './types';
import TraceDetail from './TraceDetail';

interface Props {
  completions: CompletionRecord[];
}

interface Column {
  align?: 'right';
  label: string;
}

const COLUMNS: Column[] = [
  { label: 'Use case' },
  { label: 'Status' },
  { label: 'Model' },
  { align: 'right', label: 'Prompt tokens' },
  { align: 'right', label: 'Completion tokens' },
  { align: 'right', label: 'Duration' },
  { label: 'Created' },
  { label: 'Trace' },
];

// AIDEV-NOTE: Same frame, header band, and row classes as SPADataTable; hand-rolled only because
// rows expand in place to show their trace, which the table primitive doesn't model.
const headerCellClass = cn(
  'tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-heading',
);

const headerBandClass = cn(
  'tw-bg-spa-bg-tertiary',
  'tw-border-b-2 tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-spa-border',
);

const cellClass = (isLastRow: boolean, align?: 'right') => cn(
  dataCellClass(isLastRow),
  align === 'right' && 'tw-text-right tw-tabular-nums',
);

const formatNumber = (value: number | null) => (value == null ? '—' : value.toLocaleString());

const StatusDot = ({ status }: { status: string }) => (
  <span className="tw-inline-flex tw-items-center tw-gap-1.5">
    <span
      aria-hidden
      className={cn(
        'tw-inline-block tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full',
        status === 'success' ? 'tw-bg-spa-success' : 'tw-bg-spa-error',
      )}
    />
    {status}
  </span>
);

interface CompletionRowProps {
  completion: CompletionRecord;
  isLast: boolean;
}

const CompletionRow = ({ completion, isLast }: CompletionRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasTrace = Boolean(completion.langfuse_trace_id);
  // The divider under the row moves to the detail row while it is open.
  const rowIsLast = isLast && !isExpanded;

  return (
    <>
      <TableRow
        className={cn(dataRowClass(0, false), 'tw-cursor-pointer')}
        onClick={() => setIsExpanded(open => !open)}
      >
        <TableCell className={cellClass(rowIsLast)}>
          <button
            aria-expanded={isExpanded}
            className={cn(
              'tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0',
              'tw-cursor-pointer tw-text-left tw-text-inherit',
            )}
            type="button"
          >
            <span
              aria-hidden
              className={cn(
                'tw-inline-flex tw-shrink-0 tw-text-spa-tertiary tw-transition-transform',
                !isExpanded && '-tw-rotate-90',
              )}
            >
              <Icon icon="ChevronDown" size={14} />
            </span>
            {completion.use_case || '—'}
          </button>
        </TableCell>
        <TableCell className={cellClass(rowIsLast)}>
          <StatusDot status={completion.status} />
        </TableCell>
        <TableCell className={cn(cellClass(rowIsLast), 'tw-max-w-[12rem] tw-truncate tw-text-spa-secondary')}>
          {completion.model_identifier || '—'}
        </TableCell>
        <TableCell className={cellClass(rowIsLast, 'right')}>{formatNumber(completion.prompt_tokens)}</TableCell>
        <TableCell className={cellClass(rowIsLast, 'right')}>{formatNumber(completion.completion_tokens)}</TableCell>
        <TableCell className={cellClass(rowIsLast, 'right')}>
          {completion.request_duration != null ? `${completion.request_duration.toFixed(2)}s` : '—'}
        </TableCell>
        <TableCell className={cn(cellClass(rowIsLast), 'tw-whitespace-nowrap tw-text-spa-secondary')}>
          {new Date(completion.created_at).toLocaleString()}
        </TableCell>
        <TableCell
          className={cn(
            cellClass(rowIsLast),
            'tw-text-xs',
            hasTrace ? 'tw-text-spa-accent-text' : 'tw-text-spa-tertiary',
          )}
        >
          {hasTrace ? 'View trace' : 'No trace'}
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell className={cn(dataCellClass(isLast), 'tw-pt-0')} colSpan={COLUMNS.length}>
            {completion.langfuse_trace_id ? (
              <TraceDetail traceId={completion.langfuse_trace_id} />
            ) : (
              <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">
                No Langfuse trace associated with this completion.
              </p>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const CompletionList = ({ completions }: Props) => {
  if (completions.length === 0) {
    return <p className="tw-m-0 tw-text-sm tw-text-spa-tertiary">No completions found for this model record.</p>;
  }

  return (
    <div className={dataFrameClass}>
      <Table className="tw-w-full tw-border-collapse">
        <TableHeader className={headerBandClass}>
          <TableRow>
            {COLUMNS.map(column => (
              <TableCell
                key={column.label}
                header
                className={cn(headerCellClass, column.align === 'right' && 'tw-text-right')}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {completions.map((completion, index) => (
            <CompletionRow
              key={completion.id}
              completion={completion}
              isLast={index === completions.length - 1}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CompletionList;
